import React, { Fragment } from 'react';
import {observer, useLocalObservable} from 'mobx-react';
import FormDef from '../../forms/company-generic-edit';
import GenericEdit from '../../components/company/company-generic';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { Heading1, ThemedButton } from '@awarego-ui/components';
import {useStores} from "../../hooks/use-stores";


function GenericEditContainer ({
                                   title,
                                   fields = {},
                                   dataSource = {},
                                   noSameValueCheckFields = [],
                                   updateBranding,
                                   onRequestClose,
                                   onSaved,
                                   afterSaveFn,
                                   saveFn,
                                   saving,
                                   error,
                               }) {
    const { authStore, store, brandingStore } = useStores();

    const safeFields = {};
    Object.entries(fields).forEach(([originalKey, fieldConfig]) => {
        const safeKey = originalKey.replace(/\./g, '__setting__');


        if (noSameValueCheckFields.includes(originalKey)) {
            const oldValue = dataSource[originalKey] || '';
            const oldRules = fieldConfig.rules || 'string';
            fieldConfig = {
                ...fieldConfig,
                rules: `${oldRules}|noSameValue:${oldValue}`,
            };
        }

        safeFields[safeKey] = fieldConfig;
    });

    // 2) onSuccess: transform back and save
    const onSuccess = async () => {
        const values = localStore._form.values();
        const transformedValues = { generic_settings: {} };

        Object.keys(values).forEach(safeKey => {
            const realKey = safeKey.replace(/__setting__/g, '.');
            const originalValue = dataSource[realKey];
            const currentValue = values[safeKey];

            const isChanged = JSON.stringify(originalValue) !== JSON.stringify(currentValue);

            if (isChanged) {
                transformedValues.generic_settings[realKey] = currentValue;
            }
        });

        if (Object.keys(transformedValues.generic_settings).length === 0) {
            onRequestClose(); // Nothing changed, just close modal
            return;
        }

        const save = saveFn || (async (vals) => {
            await store.saveCurrentCompany({ ...vals });
        });

        await save(transformedValues);

        if(updateBranding){
            brandingStore.updateBranding(transformedValues.generic_settings);
        }

        if (afterSaveFn) afterSaveFn();

        onSaved && onSaved(transformedValues);
    };


    // 3) Build the form
    const localStore = useLocalObservable(() => {
        const _form = new FormDef(
            safeFields,
            {}, // any additional “setup” here
            {
                hooks: { onSuccess },
            }
        );

        // Prefill data
        // e.g. dataSource => { "azure.clientSecret": "abc", "azure.tenant": "def" }
        // transform them with __setting__ so the form sees the correct keys
        const safeData = {};
        Object.entries(dataSource).forEach(([originalKey, value]) => {
            const safeKey = originalKey.replace(/\./g, '__setting__');
            safeData[safeKey] = value;
        });
        _form.update(safeData);

        return { _form };
    });

    const { trialError } = authStore;
    const err = error !== undefined ? error : store.error;
    const savingFlag = saving !== undefined ? saving : store.savingCurrentCompany;

    return (
        <Fragment>
            <div className="modalHeader">
                <Heading1>{title}</Heading1>
                <img
                    className="modal-close-right"
                    src="/static/img/close.svg"
                    alt="Close"
                    onClick={onRequestClose}
                />
            </div>
            <div className="modalContent">
                {Object.keys(safeFields).map(safeKey => {
                    const realKey = safeKey.replace(/__setting__/g, '.');
                    const fieldConfig = fields[realKey] || {};
                    return (
                        <GenericEdit
                            key={safeKey}
                            form={localStore._form}
                            error={err || trialError}
                            field={safeKey}
                            label={fieldConfig.label}
                        />
                    );
                })}
            </div>
            <div className="modalFooter">
                {savingFlag ? (
                    <ThemedSpinner size={50} />
                ) : (
                    <ThemedButton
                        mode="secondary"
                        onClick={localStore._form.onSubmit}
                        text="Done"
                    />
                )}
            </div>
        </Fragment>
    );
}

export default observer(GenericEditContainer);

